import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChoiceButton } from "@/components/ChoiceButton";
import { Loader2, Play, RefreshCw, Search, Undo2 } from "lucide-react";

/* PARTS WITH A NAME BUT NO SEARCH NAME (his call, 4 Nov): the count first, it runs only when he presses Go, logged, Undo.
   Go / Undo need the `fix_search_names` toggle (Admin only until he grants it) — without it those buttons are absent. */
const n = (x) => (x || 0).toLocaleString();

function LastRun({ run, canFix, busy, onUndo }) {
  const undone = run.status === "undone";
  return (
    <div data-testid="sn-last-run" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          Last run: <b data-testid="sn-last-run-fixed">{n(run.fixed)}</b> part(s) given their search name by {run.by} · {new Date(run.at).toLocaleString()}.
          {run.status !== "done" && <span data-testid="sn-last-run-undone"> {undone ? "Undone" : "Partly undone"} by {run.undone_by} — {n(run.undo_put_back)} put back{run.undo_left_alone?.length ? `, ${run.undo_left_alone.length} left alone (renamed since)` : ""}.</span>}
        </span>
        {canFix && !undone && (
          <ChoiceButton tone="danger" data-testid="sn-undo-run" disabled={busy} onClick={() => onUndo(null)} className="ml-auto">
            <Undo2 className="w-3.5 h-3.5 mr-1" />Undo this run
          </ChoiceButton>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200 bg-white">
        {(run.part_list || []).map((p, i) => (
          <div key={p.part_id} data-testid={`sn-run-part-${i}`} className="px-3 py-2 text-[13px] flex flex-wrap items-center gap-2">
            <a href={`/parts/${p.part_id}`} className="font-mono font-bold text-[#B01E28]">{p.short_code}</a>
            <span className="text-slate-600">{p.name}</span>
            {p.undone ? <span className="ml-auto text-xs text-slate-500">put back</span> : canFix && !undone && (
              <ChoiceButton tone="danger" data-testid={`sn-undo-part-${i}`} disabled={busy} onClick={() => onUndo(p)} className="ml-auto h-8 min-h-8">
                <Undo2 className="w-3 h-3 mr-1" />Undo this part
              </ChoiceButton>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SearchNamesCard() {
  const { has } = useAuth();
  const canFix = has("fix_search_names");
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const [seen, setSeen] = useState(false);   // counted when the card scrolls into view, not with the whole page
  const load = useCallback(() => {
    api.get("/catch-up/search-names/count").then(({ data }) => setC(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't count"));
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el || seen) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => { if (seen) load(); }, [seen, load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(ok(data)); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false); load();
  };
  const go = () => run(() => api.post("/catch-up/search-names/go", { fingerprint: c.fingerprint }), (d) => `${n(d.run.fixed)} part(s) given their search name — Undo is beside the run`);
  const undo = (p) => run(() => api.post(`/catch-up/search-names/runs/${c.last_run.id}/undo`, null, { params: p ? { part_id: p.part_id } : {} }), (d) => `${n(d.put_back)} put back${d.left_alone?.length ? `, ${d.left_alone.length} left alone` : ""}`);

  return (
    <section ref={box} data-testid="search-names-card" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="px-4 py-3 border-b-2 border-[#1F1F1F] flex items-center gap-2 flex-wrap">
        <Search className="w-5 h-5 text-[#B01E28]" />
        <h2 className="font-display font-bold text-lg text-[#1F1F1F]">Parts with a name but no search name</h2>
        <ChoiceButton data-testid="sn-recount" onClick={load} className="ml-auto"><RefreshCw className="w-3.5 h-3.5 mr-1" />Count again</ChoiceButton>
      </div>
      <div className="p-4 space-y-3 text-sm text-slate-700">
        <p>Search reads a part's name through its <b>search name</b> (the name in lower case). These live parts were saved without one; search still finds them by their name meanwhile. Go writes each one's search name exactly as a save would — nothing else on the part changes.</p>
        {!c ? <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Counting — nothing is written…</div> : (
          <>
            <p data-testid="sn-nothing-written" className="text-[12px] text-emerald-800 font-semibold">This count wrote nothing · counted in {n(c.counted_in_ms)} ms</p>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="sn-would-fix" className="rounded-xl border-2 border-slate-200 bg-white p-3">
                <div className="font-display text-2xl font-black leading-none tabular-nums">{n(c.would_fix)}</div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1.5">Live parts Go would fix</div>
              </div>
              <div data-testid="sn-archived" className="rounded-xl border-2 border-slate-200 bg-white p-3">
                <div className="font-display text-2xl font-black leading-none tabular-nums">{n(c.archived_left_alone)}</div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1.5">Archived — left alone</div>
              </div>
            </div>
            {c.sample.length > 0 && (
              <div data-testid="sn-sample" className="max-h-56 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200">
                {c.sample.map((p, i) => (
                  <div key={p.part_id} data-testid={`sn-sample-${i}`} className="px-3 py-1.5 text-[13px] flex gap-2">
                    <span className="font-mono font-bold">{p.short_code}</span><span className="text-slate-600 truncate">{p.name}</span>
                  </div>
                ))}
                {c.would_fix > c.sample.length && <div className="px-3 py-1.5 text-[11px] text-slate-500">…and {n(c.would_fix - c.sample.length)} more</div>}
              </div>
            )}
            {canFix ? (
              <Button data-testid="sn-go" disabled={busy || !c.would_fix} onClick={go}
                className="rounded-lg h-12 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}Go — fix these {n(c.would_fix)}
              </Button>
            ) : (
              <p data-testid="sn-no-permission" className="text-[12px] text-slate-500">Go and Undo need the “Fix parts with a name but no search name” toggle. Only Admin has it unless it's granted on the Permissions screen.</p>
            )}
            {c.last_run && <LastRun run={c.last_run} canFix={canFix} busy={busy} onUndo={undo} />}
          </>
        )}
      </div>
    </section>
  );
}
